import {
  HOME_VISIT_REPORT_FIELDS,
  itemsOfField,
  selectedText,
} from '@/apps/latest/components/organisms/PostRecordingView/homeVisitFields'
import { buildSoapCopyText } from '@/apps/latest/components/organisms/PostRecordingView/soapCopyText'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'

import { type HomeVisitLetterhead, type HomecareRecipient } from './homecareApi'

// 訪問報告書を紙に出す。本文は 1 つで、頭書き (宛先) だけを宛先ごとに替えて 1 枚ずつ刷る。
// 並びはアイセイの P-CUBEn 取り込み用の報告書の様式 (訪問薬剤管理指導報告書) に合わせる:
// 表題 → 宛名 → 挨拶 → 患者 → 伝達事項 → 報告 (= SOAP) → 訪問時状況 → 薬局。
// 様式の右の列 (処方) と足の住所・FAX は今は持たない。

export const REPORT_TITLE = '訪問薬剤管理指導報告書'
const GREETING =
  'いつもお世話になっております。下記ご利用者様の訪問薬剤管理指導報告書をお送りいたします。'

// 紙の種類ごとに替わる部分 (報告書と計画書で同じ別窓・同じ並びの型を使う)。
// issuedOn = 作成日 (ISO)。無ければ刷った日。
export interface PrintDocument {
  title: string
  greeting?: string
  closing?: string
  issuedOn?: string
}

export const REPORT_DOCUMENT: PrintDocument = {
  title: REPORT_TITLE,
  greeting: GREETING,
}

export interface ReportBlock {
  label: string
  text: string
}

export interface ReportPage {
  addressee: string[]
  patientLine: string
  blocks: ReportBlock[]
  footer: string[]
}

const formatDate = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}

// 生年月日 (YYYY-MM-DD) は暦の日付。Date に通すと時差で 1 日ずれるので文字のまま読む。
// 生年月日 (ISO の日付) を「1940年3月4日」に。Date を通さない (時差で 1 日ずれる)。在宅の画面と紙で同じ書き方
export const formatBirthDate = (value: string): string => {
  const [y, m, d] = value.split('-').map(Number)
  return `${y}年${m}月${d}日`
}

export const ageOn = (birthDate: string, on: Date): number => {
  const [y, m, d] = birthDate.split('-').map(Number)
  const age = on.getFullYear() - y
  const hadBirthday =
    on.getMonth() + 1 > m || (on.getMonth() + 1 === m && on.getDate() >= d)
  return hadBirthday ? age : age - 1
}

// 宛名の 2 行。処方医は「先生」、ケアマネ・その他は「様」。氏名が無ければ施設に「御中」。
export const addresseeLines = (recipient: HomecareRecipient | null) => {
  if (!recipient) return []
  if (!recipient.name) return [`${recipient.facility} 御中`]
  const honorific = recipient.kind === 'physician' ? '先生' : '様'
  return [recipient.facility, `${recipient.name} ${honorific}`].filter(Boolean)
}

export const buildReportPage = (
  event: YakurekiDetail,
  letterhead: HomeVisitLetterhead,
  recipient: HomecareRecipient | null
): ReportPage => {
  const { patient, pharmacy, pharmacist_name, visited_at } = letterhead
  const patientLine = [
    `患者氏名 ${patient.name} 様`,
    patient.birth_date
      ? `生年月日 ${formatBirthDate(patient.birth_date)} (${ageOn(
          patient.birth_date,
          new Date(visited_at)
        )}歳)`
      : '',
    patient.care_level ? `介護度 ${patient.care_level}` : '',
    `訪問日 ${formatDate(visited_at)}`,
  ]
    .filter(Boolean)
    .join('　')

  const fieldText = (key: string) =>
    selectedText(itemsOfField(event.soaps, key))
  const [physicianMessage, ...situations] = HOME_VISIT_REPORT_FIELDS
  const blocks: ReportBlock[] = [
    { label: '伝達事項', text: fieldText(physicianMessage.key) },
    { label: '報告', text: buildSoapCopyText(event.soaps) },
    ...situations.map(({ key, label }) => ({ label, text: fieldText(key) })),
  ].filter((block) => block.text)

  return {
    addressee: addresseeLines(recipient),
    patientLine,
    blocks,
    footer: [
      [
        pharmacy.name,
        pharmacy.phone_number ? `TEL ${pharmacy.phone_number}` : '',
      ]
        .filter(Boolean)
        .join('　'),
      pharmacist_name ? `薬剤師 ${pharmacist_name}` : '',
    ].filter(Boolean),
  }
}

const PAGE_CSS = `
@page { size: A4; margin: 16mm 18mm; }
body { font-family: sans-serif; color: black; margin: 0; }
.page { break-after: page; font-size: 10.5pt; line-height: 1.6; }
.page:last-child { break-after: auto; }
.head { display: flex; justify-content: space-between; align-items: baseline; }
.title { font-size: 16pt; font-weight: bold; }
.addressee { margin-top: 12px; font-size: 12pt; }
.greeting { margin-top: 12px; }
.patient { margin-top: 12px; padding: 6px 0; border-top: 1px solid black; border-bottom: 1px solid black; }
.block { margin-top: 10px; }
.label { font-weight: bold; }
.text { white-space: pre-wrap; }
.footer { margin-top: 20px; text-align: right; }
`

// 別窓に宛先の数だけ頁を組んで印刷する (先例: 遠隔服薬指導の番号札)。
// 中身は記録の文字列なので HTML として書かず textContent で入れる。
export const printReportPages = (
  pages: ReportPage[],
  document: PrintDocument = REPORT_DOCUMENT
): boolean => {
  const win = window.open('', '_blank', 'width=794,height=1123')
  if (!win) return false
  const doc = win.document
  doc.title = document.title
  const style = doc.createElement('style')
  style.textContent = PAGE_CSS
  doc.head.append(style)

  const el = (parent: HTMLElement, className: string, text = '') => {
    const node = doc.createElement('div')
    node.className = className
    node.textContent = text
    parent.append(node)
    return node
  }
  const issuedOn = formatDate(document.issuedOn ?? new Date().toISOString())

  pages.forEach((page) => {
    const root = el(doc.body, 'page')
    const head = el(root, 'head')
    el(head, 'title', document.title)
    el(head, 'date', `作成日 ${issuedOn}`)
    if (page.addressee.length > 0) {
      const addressee = el(root, 'addressee')
      page.addressee.forEach((line) => el(addressee, '', line))
    }
    if (document.greeting) el(root, 'greeting', document.greeting)
    el(root, 'patient', page.patientLine)
    page.blocks.forEach((block) => {
      const node = el(root, 'block')
      el(node, 'label', `【${block.label}】`)
      el(node, 'text', block.text)
    })
    if (document.closing) el(root, 'greeting', document.closing)
    const footer = el(root, 'footer')
    page.footer.forEach((line) => el(footer, '', line))
  })

  win.focus()
  win.print()
  return true
}
